import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Button, Card, Col, Input, InputNumber, Row } from "antd";

// Input editable de "Nuevo precio de venta" en la tabla de productos de Subsanar.
export const SubsanarPriceInput = styled(InputNumber)`
  && { width: 150px; border-radius: 8px; }
  &&:not(.ant-input-number-status-error) { border-color: #cbd5e1; }
  &&:hover:not(.ant-input-number-status-error),
  &&.ant-input-number-focused:not(.ant-input-number-status-error) { border-color: #2563eb; box-shadow: none; }
  && .ant-input-number-input { text-align: right; font-weight: 700; color: #0f172a; }
`;

export const DetailViewRoot = styled.div`display: grid; grid-template-columns: minmax(0, 1fr); gap: 16px; min-width: 0;`;
export const DetailBackButton = styled(Button)`
  && { width: fit-content; height: 40px; border-radius: 12px; border-color: #cbd5e1; background: #ffffff; color: #334155; font-weight: 700; box-shadow: none; }
`;

/**
 * Grid de tarjetas meta (cabecera de detalle).
 * Tallajes AntD: xs 1 col · md 2 cols · xl 4 cols.
 */
export const DetailTopRow = styled(Row)`width: 100%;`;
export const DetailTopCol = styled(Col)`min-width: 0;`;
export const DetailMetaCard = styled(Card)`
  border-radius: 18px; border: 1px solid #dbe4f0; box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  .ant-card-body { padding: 16px 18px; display: grid; gap: 6px; }
`;
export const DetailMetaLabel = styled.span`color: #64748b; font-size: 0.78rem; font-weight: 700;`;
export const DetailMetaValue = styled.span`color: #0f172a; font-size: 0.95rem; font-weight: 800;`;
/**
 * Cuerpo de detalle a dos columnas (principal + lateral).
 * Tallajes AntD: xs 1 col apilada · lg 2 cols (~55/45).
 * min-width:0 evita que tablas/textos largos desborden el Row.
 */
export const DetailBodyRow = styled(Row)`width: 100%;`;
export const DetailBodyCol = styled(Col)`
  min-width: 0;
  display: grid;
  /* Pista con minmax(0,1fr): la columna respeta el ancho de la Col de AntD y su
     contenido ancho (tablas, nombres largos) hace scroll en vez de desbordarse
     y superponerse a la columna vecina. */
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  align-content: start;
`;
export const DetailSectionCard = styled(Card)`
  border-radius: 20px; border: 1px solid #dbe4f0; box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
  /* min-width:0 + pista minmax(0,1fr): las tablas anchas hacen scroll interno en
     lugar de estirar la tarjeta y desbordar la columna. */
  min-width: 0;
  .ant-card-body { padding: 18px; display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; }
`;
export const DetailSectionTitle = styled.h3`margin: 0; font-size: 1rem; font-weight: 800; color: #0f172a;`;
export const DetailDocsList = styled.div`display: grid; gap: 12px;`;
export const DetailDocItem = styled.div`
  display: grid; grid-template-columns: 1fr auto; gap: 12px; align-items: center;
  border: 1px solid #dbe4f0; border-radius: 16px; padding: 14px 16px; background: #f8fafc;
`;
export const DetailDocInfo = styled.div`display: grid; gap: 4px; min-width: 0;`;
export const DetailDocName = styled.span`color: #1e3a8a; font-size: 0.93rem; font-weight: 800; word-break: break-word;`;
export const DetailDocMeta = styled.span`color: #64748b; font-size: 0.8rem;`;
export const DetailDocActions = styled.div`display: flex; gap: 8px; flex-wrap: nowrap;`;
export const DetailDocButton = styled(Button)`
  && { height: 38px; min-width: 42px; border-radius: 10px; border-color: #c7d2fe; color: #1d4ed8; background: #ffffff; box-shadow: none; }
`;
export const DetailObservationBox = styled.div`
  min-height: 88px; border: 1px solid #dbe4f0; border-radius: 14px; padding: 14px 16px;
  background: #f8fafc; color: #0f172a; line-height: 1.6;
  overflow-wrap: anywhere; word-break: break-word;
`;
export const DetailTimeline = styled.div`
  display: grid; gap: 12px; min-width: 0;
  ${({ $scroll }) =>
    $scroll ? "max-height: 360px; overflow-y: auto; padding-right: 6px;" : ""}
`;
// Paleta de 3 tonos que se alterna por cada evento de la traza.
const TIMELINE_TONES = {
  blue: { background: "#eff6ff", border: "#1d4ed8" },
  green: { background: "#e8f8ee", border: "#04995b" },
  amber: { background: "#fff7ed", border: "#f97316" },
};
export const DetailTimelineItem = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr); gap: 6px; padding: 14px 16px; border-radius: 16px;
  min-width: 0;
  background: ${({ $tone }) => (TIMELINE_TONES[$tone] ?? TIMELINE_TONES.blue).background};
  border-left: 4px solid ${({ $tone }) => (TIMELINE_TONES[$tone] ?? TIMELINE_TONES.blue).border};
`;
const timelineShimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;
export const DetailTimelineSkeletonItem = styled.div`
  display: grid; gap: 8px; padding: 14px 16px; border-radius: 16px;
  background: #f8fafc; border-left: 4px solid #e2e8f0;
`;
export const DetailTimelineSkeletonLine = styled.div`
  height: ${({ $small }) => ($small ? "10px" : "14px")};
  width: ${({ $width }) => $width ?? "100%"};
  border-radius: 6px;
  background: linear-gradient(90deg, #e2e8f0 25%, #eef2f7 37%, #e2e8f0 63%);
  background-size: 400px 100%;
  animation: ${timelineShimmer} 1.4s ease infinite;
`;
export const DetailTimelineTitle = styled.span`color: #0f172a; font-size: 0.95rem; font-weight: 800; overflow-wrap: anywhere; word-break: break-word;`;
export const DetailTimelineMeta = styled.span`color: #64748b; font-size: 0.78rem; font-weight: 700; overflow-wrap: anywhere; word-break: break-word;`;export const ReviewActionsRow = styled.div`display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap;`;
const reviewBase = `&& { height: 40px; min-width: 140px; border-radius: 12px; font-weight: 800; box-shadow: none; }`;
export const WithObservationButton = styled(Button)`${reviewBase} && { border-color: #f97316; background: #f97316; color: #ffffff; }`;
export const WithoutObservationButton = styled(Button)`${reviewBase} && { border-color: #16a34a; background: #16a34a; color: #ffffff; }`;
export const ReviewPanel = styled(Card)`
  border-radius: 20px; border: 2px solid #dbe4f0; box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
  .ant-card-body { padding: 18px; display: grid; gap: 14px; }
`;
export const ReviewFieldLabel = styled.label`color: #334155; font-size: 0.82rem; font-weight: 800;`;
export const ReviewTextArea = styled(Input.TextArea)`
  && { border-radius: 14px; border-color: #cbd5e1; min-height: 120px; resize: vertical; padding: 12px 14px; }
`;
export const ReviewUploadBox = styled.label`display: grid; gap: 8px; border: 1px solid #cbd5e1; border-radius: 14px; padding: 12px 14px; background: #ffffff; cursor: pointer;`;
export const ReviewUploadInput = styled.input`display: none;`;
export const ReviewUploadText = styled.span`color: #475569; font-size: 0.84rem;`;
export const ReviewSubmitRow = styled.div`display: flex; justify-content: flex-end;`;
export const ReviewSubmitButton = styled(Button)`
  && { height: 40px; min-width: 180px; border-radius: 12px; border-color: #f97316; background: #f97316; color: #ffffff; font-weight: 800; box-shadow: none; }
`;
/**
 * Formulario de subsanación a dos columnas.
 * Tallajes AntD: xs 1 col · lg 2 cols.
 */
export const SubsanarFormRow = styled(Row)`width: 100%;`;
export const SubsanarFormCol = styled(Col)`min-width: 0;`;
export const SubsanarDocCard = styled.div`
  display: grid; grid-template-columns: 1fr auto; gap: 10px; align-items: center;
  border: 1px solid #dbe4f0; border-radius: 14px; padding: 14px 16px; background: #f8fafc;
`;
export const SubsanarDocActions = styled.div`display: flex; gap: 8px; flex-shrink: 0;`;
export const ReemplazarButton = styled(Button)`
  && { height: 36px; border-radius: 10px; border-color: #1d4ed8; background: #ffffff; color: #1d4ed8; font-weight: 700; font-size: 0.82rem; box-shadow: none; }
  &&:hover, &&:focus { background: #eff6ff; border-color: #1e40af; color: #1e40af; }
`;
export const SubsanarProductsHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px;
`;
export const SubsanarProductsActions = styled.div`display: flex; gap: 8px;`;
export const AddProductButton = styled(Button)`
  && { height: 38px; border-radius: 10px; border-color: #1d4ed8; background: #1d4ed8; color: #ffffff; font-weight: 700; box-shadow: none; }
  &&:hover, &&:focus { background: #1e40af; border-color: #1e40af; color: #ffffff; }
`;
export const SubsanarBottomRow = styled.div`display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap;`;
export const CancelButton = styled(Button)`
  && { height: 40px; min-width: 120px; border-radius: 12px; border-color: #cbd5e1; background: #ffffff; color: #334155; font-weight: 800; box-shadow: none; }
`;
export const ReenviarButton = styled(Button)`
  && { height: 40px; min-width: 190px; border-radius: 12px; border-color: #16a34a; background: #16a34a; color: #ffffff; font-weight: 800; box-shadow: none; }
  &&:hover, &&:focus { border-color: #15803d; background: #15803d; }
`;